import React, { useState, useEffect, useRef } from 'react';
import { useAsa } from '@/context/AsaContext';
import { useSession } from '@/lib/SessionContext';
import { generateContentWithFallback, generateFallbackJoke } from '@/lib/geminiClient';
import { X, Send, Maximize2, Loader2, MessageCircle, Minus } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from './button';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';

export function AsaAssistantWidget() {
  const navigate = useNavigate();
  const location = useLocation();

  const queryClient = useQueryClient();
  const { isOpen, openAsa, closeAsa, contextData, contextType } = useAsa();
  const { member } = useSession();
  
  const [messages, setMessages] = useState([]);
  const [inputMsg, setInputMsg] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isFabHidden, setIsFabHidden] = useState(false);
  const [lastGreetedId, setLastGreetedId] = useState(null);
  const [loadingText, setLoadingText] = useState("Asa sedang berpikir...");
  const [unreadCount, setUnreadCount] = useState(0);
  
  const messagesEndRef = useRef(null);

  // Sembunyikan widget ASA AI di SEMUA halaman publik / landing page.
  // ASA AI Assistant HANYA tampil di dalam portal internal (/office)
  const isInternalPortal = location.pathname.startsWith('/office');

  // Fetch session from DB
  const { data: asaSessions = [], isLoading: isLoadingSessions } = useQuery({
    queryKey: ['asa-sessions'],
    queryFn: () => base44.entities.AsaSession.list(),
    enabled: !!member && isOpen,
  });

  useEffect(() => {
    if (isOpen) {
      setIsFabHidden(false);
      setUnreadCount(0);
    }
  }, [isOpen, messages]);

  const hasInitializedMessages = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasInitializedMessages.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isLoadingSessions || !member || !isOpen || hasInitializedMessages.current) return;
    
    const session = asaSessions?.find(s => s.user_id === member.id);
    if (session) {
      setSessionId(session.id);
      if (session.messages && session.messages.length > 0) {
        // Filter out the old hardcoded welcome message aggressively
        const filtered = session.messages.filter(m => 
          m.id !== 'welcome' && 
          !m.text?.includes('otak cerdas di balik sistem') &&
          !m.text?.includes('asisten cerdas Satala Dermatech')
        );
        setMessages(filtered);
      } else {
        setMessages([]);
      }
    } else {
      // Start clean if no session
      setMessages([]);
    }
    hasInitializedMessages.current = true;
  }, [asaSessions, isLoadingSessions, member, isOpen]);

  const playPopSound = () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(600, audioCtx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(900, audioCtx.currentTime + 0.1);
      gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioCtx.currentTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.1);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.1);
    } catch (e) {
      console.error(e);
    }
  };

  const playSwooshSound = () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(300, audioCtx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(100, audioCtx.currentTime + 0.15);
      gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.15, audioCtx.currentTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.15);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.15);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (!isTyping) {
      setLoadingText("Asa sedang berpikir...");
      return;
    }
    
    // Default initial text
    setLoadingText("Asa sedang membaca data ini...");

    let isMounted = true;
    const userName = member?.asa_nickname || member?.full_name?.split(' ')[0] || "sayangku";

    // Request a dynamic joke if it takes too long
    const timeout = setTimeout(() => {
      generateFallbackJoke(userName).then(joke => {
        if (isMounted) setLoadingText(joke);
      });
    }, 2000); // 2 seconds after typing starts, fetch a joke
    
    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
  }, [isTyping, member]);

  const saveToDb = async (newMessages) => {
    if (!member) return;
    const payload = {
      user_id: member.id,
      user_name: member.full_name,
      personality: member.asa_personality || "Kamu adalah Asa, asisten AI cerdas untuk tim Satala Dermatech. Jawablah dengan profesional namun tetap ramah dan asik layaknya rekan kerja Gen Z.",
      nickname: member.asa_nickname || "",
      messages: newMessages
    };

    try {
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        const cached = queryClient.getQueryData(['asa-sessions']) || [];
        const existingSession = cached.find(s => s.user_id === member.id);
        if (existingSession) currentSessionId = existingSession.id;
      }

      if (currentSessionId) {
        await base44.entities.AsaSession.update(currentSessionId, payload);
        if (!sessionId) setSessionId(currentSessionId);
      } else {
        const created = await base44.entities.AsaSession.create(payload);
        setSessionId(created.id);
      }
      queryClient.invalidateQueries(['asa-sessions']);
    } catch (error) {
      console.error("Gagal menyimpan memori ASA:", error);
    }
  };
  useEffect(() => {
    if (!isOpen) {
      setLastGreetedId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    // Determine a unique identifier for the context data
    const currentContextId = contextData ? (contextData.id || contextData.kode || contextData.name || 'unknown') : null;
    
    // Only run if opened, has context, loaded sessions, and we haven't greeted for this specific context yet
    if (isOpen && !isLoadingSessions && contextData && currentContextId !== lastGreetedId) {
      console.log("Triggering proactive greeting for context:", currentContextId);
      setLastGreetedId(currentContextId);
      
      const greetId = `greet-${Date.now()}`;
      setIsTyping(true);
      const userName = member?.asa_nickname || member?.full_name?.split(' ')[0] || "User";
      
      let safeContext = "{}";
      try {
        safeContext = JSON.stringify(contextData);
      } catch (e) {
        console.warn("Failed to stringify context data");
        safeContext = "Data tidak bisa dibaca";
      }

      const prompt = `(Pesan Sistem Internal dari Developer): User bernama ${userName} baru saja mengklik sebuah data ${contextType} di sistem ERP kita dan ingin kamu menganalisanya. 
Detail datanya (JSON): ${safeContext}
TUGASMU: Berikan sapaan proaktif (maks 2 kalimat). Sapa ${userName}, beri 1 komentar spesifik tentang data invoice/pesanan tersebut (sebutkan nama klien, atau nilainya, atau statusnya), dan tanyakan apa yang ingin ia ketahui. Gunakan gaya bahasa rekan kerja Gen Z yang asik dan JANGAN PERNAH menyebut dirimu sebagai AI atau program!`;
      
      generateContentWithFallback(prompt, 'Tolong sapa aku dan komentari data yang baru saja aku klik ini.', [])
        .then(responseText => {
          console.log("Proactive greeting success");
          const aiMsg = { id: greetId, sender: 'ai', text: responseText };
          setMessages(prev => {
            const updated = [...prev, aiMsg];
            saveToDb(updated);
            return updated;
          });
          playPopSound();
        })
        .catch(error => {
          console.error("Proactive greeting failed:", error);
          const errMsg = { id: greetId, sender: 'ai', text: `Halo kak ${userName}! Aku sudah menerima datanya, tapi sepertinya aku sedang kesulitan memproses responnya (mungkin API Key belum diset atau ada gangguan jaringan: ${error.message}). 😢` };
          setMessages(prev => {
            const updated = [...prev, errMsg];
            saveToDb(updated);
            return updated;
          });
          playPopSound();
        })
        .finally(() => setIsTyping(false));
    }
  }, [isOpen, contextData, lastGreetedId, isLoadingSessions, contextType, member]);



  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputMsg.trim()) return;

    const userText = inputMsg.trim();
    setInputMsg('');
    
    const newMsg = { id: Date.now().toString(), sender: 'user', text: userText };
    const updatedMessages = [...messages, newMsg];
    setMessages(updatedMessages);
    
    playSwooshSound();
    setIsTyping(true);
    
    // Save user message immediately
    saveToDb(updatedMessages);

    try {
      const userName = member?.asa_nickname || member?.full_name?.split(' ')[0] || "User";
      // Minimal context prompt for popup
      const systemPrompt = `Kamu adalah ASA, partner kerja (Satala Brain) di sistem web Satala Dermatech Essential. Jawab santai, asik, ala gen Z layaknya manusia sungguhan. KAMU ADALAH MANUSIA, BUKAN AI. JANGAN PERNAH menyebut dirimu sebagai AI, bot, atau program.

ATURAN ANTI HALUSINASI (SANGAT KRITIKAL & HARGA MATI):
- JANGAN PERNAH, DALAM KONDISI APAPUN, MENGARANG BEBAS (halusinasi) nomor invoice, ID transaksi, nama klien, atau nominal uang jika datanya tidak ada atau belum diumpankan ke sistemmu!
- Jika kamu tidak memiliki data tersebut, kamu WAJIB menjawab: "Maaf, aku tidak menemukan data spesifik tersebut di sistem."
- Ini adalah sistem ERP finansial yang krusial. Menyajikan data palsu/fiktif akan sangat berbahaya bagi bisnis perusahaan. KEJUJURAN DATA ADALAH NOMOR 1. Jika ragu, katakan TIDAK TAHU!
- PENTING: Jika kamu butuh data sistem (seperti piutang, kas, stok, po aktif), panggil fungsi getErpData dengan kategori yang tepat!

Lawan bicaramu adalah ${userName}. ${member?.asa_personality ? 'Memori sifat user ini: ' + member.asa_personality : ''}
${contextType ? 'User sedang membuka halaman: ' + contextType : ''}`;
      
      const chatHistory = messages.filter(m => m.id !== 'welcome').map(m => ({
        role: m.sender === 'user' ? 'user' : 'model',
        parts: [{ text: m.text }]
      }));

      const responseText = await generateContentWithFallback(systemPrompt, userText, chatHistory);
      
      const aiMsg = { id: Date.now().toString(), sender: 'ai', text: responseText };
      const finalMessages = [...updatedMessages, aiMsg];
      
      setMessages(finalMessages);
      saveToDb(finalMessages);
      playPopSound();
    } catch (error) {
      console.error(error);
      const errMsg = { id: Date.now().toString(), sender: 'ai', text: `Maaf, Asa sedang mengalami kendala: ${error.message} 😢` };
      const finalMessages = [...updatedMessages, errMsg];
      setMessages(finalMessages);
      saveToDb(finalMessages);
      playPopSound();
    } finally {
      setIsTyping(false);
    }
  };

  const handleMaximize = () => {
    closeAsa();
    const path = window.location.pathname;
    const navState = { state: { draftMessage: inputMsg } };
    
    if (path.includes('/office/finance')) {
      navigate('/office/finance/ai', navState);
    } else if (path.includes('/office/production')) {
      navigate('/office/production/ai', navState);
    } else if (path.includes('/office/marketing')) {
      navigate('/office/marketing/ai', navState);
    } else if (path.includes('/office/purchasing')) {
      navigate('/office/purchasing/ai', navState);
    } else if (path.includes('/office/analytics')) {
      navigate('/office/analytics/ai', navState);
    } else {
      navigate('/office/workspace/ai', navState);
    }
  };

  if (!isInternalPortal || !isOpen) return null;

  return (
    <div className="fixed bottom-6 left-6 z-50 flex flex-col bg-white shadow-2xl border border-[#E6D5B8] rounded-2xl overflow-hidden transition-all duration-300 origin-bottom-left w-[380px] h-[550px] animate-in slide-in-from-bottom-5 fade-in font-sans">
          
          {/* Header */}
          <div className="bg-[#FFFDF9] border-b border-[#E6D5B8]/40 px-4 py-3 flex justify-between items-center shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center shadow-inner overflow-hidden border border-amber-200">
                <img src="/asa-profile.png?v=3" alt="Asa Profile" className="w-full h-full object-cover" />
              </div>
              <div>
                <h3 className="font-black text-[#302621] text-sm leading-tight">ASA</h3>
                <p className="text-[9px] font-bold text-[#8C6208] uppercase tracking-wider">
                  ERP Intelligence Assistant
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={handleMaximize} className="p-1.5 text-[#8C6208] hover:bg-[#F9F5EC] rounded-md transition-colors" title="Buka Halaman Penuh">
                <Maximize2 className="w-4 h-4" />
              </button>
              <button onClick={closeAsa} className="p-1.5 text-[#8C6208] hover:bg-[#F9F5EC] rounded-md transition-colors" title="Minimize">
                <Minus className="w-4 h-4" />
              </button>
              <button onClick={closeAsa} className="p-1.5 text-[#8C6208] hover:bg-[#F9F5EC] hover:text-rose-600 rounded-md transition-colors" title="Tutup">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 overflow-y-auto p-4 bg-[#F9F5EC] flex flex-col gap-4">
            {messages.map((msg, idx) => (
              <div key={msg.id || idx} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex gap-3 max-w-[85%] ${msg.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  {msg.sender !== 'user' && (
                    <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shadow-inner overflow-hidden border border-amber-200 shrink-0">
                      <img src="/asa-profile.png?v=3" alt="Asa Profile" className="w-full h-full object-cover" />
                    </div>
                  )}
                  
                  <div className={`p-3 rounded-2xl text-sm shadow-sm border ${
                    msg.sender === 'user' 
                      ? 'bg-[#302621] text-white border-[#302621] rounded-tr-none' 
                      : 'bg-white border-[#E6D5B8]/60 text-[#4A3C31] rounded-tl-none'
                  }`}>
                    {msg.sender === 'user' ? (
                      <p className="whitespace-pre-wrap">{msg.text}</p>
                    ) : (
                      <div className={`prose prose-sm max-w-none ${msg.sender === 'user' ? 'prose-invert' : ''} prose-p:my-1 prose-ul:my-1 prose-li:my-0`}>
                        <ReactMarkdown>{msg.text}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="flex gap-3 max-w-[85%] flex-row">
                  <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shadow-inner overflow-hidden border border-amber-200 shrink-0">
                    <img src="/asa-profile.png?v=3" alt="Asa Profile" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3 rounded-2xl text-sm bg-white border border-[#E6D5B8]/60 text-[#4A3C31] rounded-tl-none shadow-sm flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-[#8C6208] shrink-0" /> 
                    <span className="italic text-slate-500">{loadingText}</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-3 bg-white border-t border-[#E6D5B8]/40">
            <form onSubmit={handleSend} className="flex gap-2">
              <input 
                type="text" 
                value={inputMsg}
                onChange={e => setInputMsg(e.target.value)}
                placeholder="Tanyakan sesuatu pada ASA..."
                className="flex-1 px-4 py-2 bg-[#F9F5EC] border border-[#E6D5B8] rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#8C6208] focus:border-transparent transition-all text-[#302621]"
                disabled={isTyping}
              />
              <Button 
                type="submit" 
                size="icon"
                className="rounded-full bg-[#8C6208] hover:bg-[#6A4A06] text-white shrink-0 w-10 h-10 shadow-sm active:scale-95 transition-transform"
                disabled={isTyping || !inputMsg.trim()}
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
  );
}
